import i18n from "@/i18n";

export function flattenStrings(value: unknown, prefix = ""): Record<string, string> {
  if (typeof value === "string") {
    return prefix ? { [prefix]: value } : {};
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const next = prefix ? `${prefix}.${key}` : key;
    Object.assign(out, flattenStrings(child, next));
  }
  return out;
}

export function unflattenStrings(flat: Record<string, string>): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split(".").filter(Boolean);
    if (parts.length === 0) continue;
    let cursor = root;
    for (let index = 0; index < parts.length - 1; index += 1) {
      const part = parts[index];
      const current = cursor[part];
      if (!current || typeof current !== "object" || Array.isArray(current)) {
        cursor[part] = {};
      }
      cursor = cursor[part] as Record<string, unknown>;
    }
    cursor[parts[parts.length - 1]] = value;
  }
  return root;
}

export function bundleStrings(locale: "en" | "ms"): Record<string, string> {
  const bundle = i18n.getResourceBundle(locale, "translation") as unknown;
  return flattenStrings(bundle);
}

export function applyCmsTranslationRows(
  rows: Array<{ locale: string; key: string; value: string }>,
): void {
  const grouped: Record<string, Record<string, string>> = {};
  for (const row of rows) {
    if (!row.locale || !row.key) continue;
    grouped[row.locale] ??= {};
    grouped[row.locale][row.key] = row.value ?? "";
  }
  for (const [locale, flat] of Object.entries(grouped)) {
    i18n.addResourceBundle(locale, "translation", unflattenStrings(flat), true, true);
  }
}

export async function loadCmsTranslations(): Promise<void> {
  if (typeof window === "undefined") return;
  const { supabase } = await import("@/integrations/supabase/client");
  const { data, error } = await supabase
    .from("cms_translations")
    .select("locale, key, value");
  if (error || !data) return;
  applyCmsTranslationRows(data);
}
